import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Stack } from '@mui/material';
import {
  FieldSelect,
  FieldToggle,
  FormDialog,
  LoadingSkeleton,
  useToast,
  type SelectOption,
} from '../../components';
import { SupportedLanguages, type SupportedLanguage } from '../../i18n/config';
import { useUserLanguage } from '../../i18n/useUserLanguage';
import { useMe, useUpdatePreferences } from '../api/me';
import { preferencesFormSchema, type PreferencesFormValues } from '../schemas/preferencesForm';
import { timeZoneOptionValues } from '../timeZones';
import type { MePreferencesRequest, MeResponse } from '../types';
import '../i18n';

export interface PreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

function narrowLanguage(value: string, fallback: SupportedLanguage): SupportedLanguage {
  return (SupportedLanguages as readonly string[]).includes(value)
    ? (value as SupportedLanguage)
    : fallback;
}

function toDefaults(
  me: MeResponse | undefined,
  fallbackLang: SupportedLanguage,
): PreferencesFormValues {
  return {
    preferredLanguage: me ? narrowLanguage(me.preferredLanguage, fallbackLang) : fallbackLang,
    preferredTimeZone: me?.preferredTimeZone ?? '',
    receivesUsageAlerts: me?.receivesUsageAlerts ?? true,
    receivesBillingReports: me?.receivesBillingReports ?? true,
  };
}

/**
 * Diffs the form against the loaded profile and returns only the changed fields
 * (partial merge). Time zone `''` means "use browser default"; the server can't
 * clear a stored zone back to null via partial merge, so clearing (concrete →
 * `''`) sends nothing — a documented limitation (see the slice log).
 */
function buildChangedFields(values: PreferencesFormValues, me: MeResponse): MePreferencesRequest {
  const body: MePreferencesRequest = {};
  if (values.preferredLanguage !== me.preferredLanguage) {
    body.preferredLanguage = values.preferredLanguage;
  }
  const originalZone = me.preferredTimeZone ?? '';
  if (values.preferredTimeZone !== originalZone && values.preferredTimeZone !== '') {
    body.preferredTimeZone = values.preferredTimeZone;
  }
  if (values.receivesUsageAlerts !== me.receivesUsageAlerts) {
    body.receivesUsageAlerts = values.receivesUsageAlerts;
  }
  if (values.receivesBillingReports !== me.receivesBillingReports) {
    body.receivesBillingReports = values.receivesBillingReports;
  }
  return body;
}

/**
 * The self-service Preferences form: language / time zone / usage-alerts /
 * billing-reports. Pre-fills from `GET /me`, sends only changed fields to
 * `PUT /me/preferences`, toasts on success, and — on a language change —
 * switches the live i18next locale (the preferences hook already invalidates
 * `['me']` and `['whoami']`). Launched from My profile or the user menu.
 */
export function PreferencesDialog({ open, onClose }: PreferencesDialogProps) {
  const { t } = useTranslation('profile');
  const toast = useToast();
  const { language, timeZone, setLanguage } = useUserLanguage();

  // Only fetch when the dialog is open (it is always mounted by the provider).
  const meQuery = useMe(open);
  const me = meQuery.data;
  const mutation = useUpdatePreferences();

  const { control, handleSubmit, reset } = useForm<PreferencesFormValues>({
    resolver: zodResolver(preferencesFormSchema),
    defaultValues: toDefaults(undefined, language),
  });

  // Selecting "browser default" ('') when a concrete zone is stored can't be
  // persisted via the partial-merge endpoint (omitting the field leaves the old
  // value; there's no clear semantics — TD-200). Warn the user rather than let a
  // "Preferences saved" toast imply the clear took effect.
  const watchedTimeZone = useWatch({ control, name: 'preferredTimeZone' });
  const timeZoneClearUnsupported = Boolean(me?.preferredTimeZone) && watchedTimeZone === '';

  // Reset to the loaded profile whenever the dialog opens or the profile
  // arrives. `reset` is RHF-internal (not React setState), so this effect stays
  // clear of the set-state-in-effect rule (mirrors UserFormDialog).
  useEffect(() => {
    if (!open || !me) return;
    reset(toDefaults(me, language));
  }, [open, me, language, reset]);

  const languageOptions: SelectOption[] = useMemo(
    () => SupportedLanguages.map((lng) => ({ value: lng, label: t(`languageOptions.${lng}`) })),
    [t],
  );

  const timeZoneOptions: SelectOption[] = useMemo(() => {
    const browserDefault: SelectOption = {
      value: '',
      label: t('preferences.timeZoneBrowserDefault', { zone: timeZone }),
    };
    const zones = timeZoneOptionValues(me?.preferredTimeZone ?? null).map((tz) => ({
      value: tz,
      label: tz,
    }));
    return [browserDefault, ...zones];
  }, [t, timeZone, me?.preferredTimeZone]);

  const close = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const onValid = (values: PreferencesFormValues) => {
    if (!me) return;
    const body = buildChangedFields(values, me);
    mutation.mutate(body, {
      onSuccess: () => {
        // A language change flips the live i18next locale (values.preferredLanguage
        // is the narrowed SupportedLanguage; body carries the same code).
        if (body.preferredLanguage) {
          void setLanguage(values.preferredLanguage);
        }
        toast.success(t('preferences.saved'));
        onClose();
      },
      onError: () => {
        toast.error(t('preferences.errorTitle'));
      },
    });
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={t('preferences.title')}
      onSubmit={handleSubmit(onValid)}
      submitLabel={t('preferences.save')}
      cancelLabel={t('preferences.cancel')}
      submitPending={mutation.isPending}
      submitDisabled={!me}
    >
      {!me ? (
        <LoadingSkeleton variant="detail" rows={4} />
      ) : (
        <Stack spacing={2.5}>
          <FieldSelect
            control={control}
            name="preferredLanguage"
            label={t('preferences.fields.language')}
            options={languageOptions}
          />
          <FieldSelect
            control={control}
            name="preferredTimeZone"
            label={t('preferences.fields.timeZone')}
            options={timeZoneOptions}
            helperText={t('preferences.hints.timeZone')}
          />
          {timeZoneClearUnsupported && (
            <Alert severity="info" sx={{ mt: -1 }}>
              {t('preferences.timeZoneClearUnavailable')}
            </Alert>
          )}
          <FieldToggle
            control={control}
            name="receivesUsageAlerts"
            label={t('preferences.fields.usageAlerts')}
            description={t('preferences.hints.usageAlerts')}
          />
          <FieldToggle
            control={control}
            name="receivesBillingReports"
            label={t('preferences.fields.billingReports')}
            description={t('preferences.hints.billingReports')}
          />
        </Stack>
      )}
    </FormDialog>
  );
}
